import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'openReplay'

export type OpenReplayOption = {
  _id: string
  display: string
  [key: string]: unknown
}

export type OpenReplaySession = {
  sessionId: string
  eventsCount: number
  startTs: number
  endTs?: number
  replayUrl?: string | null
  userId: string
  resource: {
    _id: string
    firstname?: string
    lastname?: string
    display: string
    picture?: string
  }
  metadata: {
    resourceId: string
    firmId: string
    jobId?: string | null
    job?: string | null
  }
}

type OpenReplayListResponse = {
  docs: OpenReplaySession[]
  count: {
    total: number
    currentPage: number
    totalPages: number
  }
}

type OpenReplayOptionsResponse = {
  docs: OpenReplayOption[]
}

export type OpenReplayVideoResponse = {
  status: 'ready' | 'pending' | 'failure' | 'unavailable' | string
  videoUrl?: string
  message?: string
}

export type OpenReplayReplayEvent = Record<string, any>

export type OpenReplayReplayResponse = {
  status: 'ready' | 'unavailable' | string
  replay?: {
    session: {
      sessionId: string
      projectId?: string | number
      platform?: string
      startTs?: number
      duration?: number
      timezone?: string
      userId?: string
      userBrowser?: string
      userOs?: string
      userDevice?: string
      userCountry?: string
      screenWidth?: number
      screenHeight?: number
      pagesCount?: number
      eventsCount?: number
      errorsCount?: number
      metadata?: Record<string, unknown>
      files?: {
        dom?: number
        devtools?: number
        canvas?: number
        mobileFrames?: number
      }
    }
    events: {
      events: OpenReplayReplayEvent[]
      userEvents: OpenReplayReplayEvent[]
      errors: OpenReplayReplayEvent[]
      crashes: OpenReplayReplayEvent[]
      issues: OpenReplayReplayEvent[]
      stackEvents: OpenReplayReplayEvent[]
      incidents: OpenReplayReplayEvent[]
      userTesting: OpenReplayReplayEvent[]
    }
  }
  message?: string
}

export type OpenReplayProxyResponse = {
  expiresAt: number
  embedUrl: string
  apiBaseUrl: string
  fileBaseUrl: string
}

type ListSessionsQuery = {
  page: number
  limit: number
  search?: string
  jobId?: string
  resourceId?: string
  startTimestamp?: number
  endTimestamp?: number
  sortOrder?: 'asc' | 'desc'
  sessionsPerUser?: number
}

type FilterQuery = {
  search?: string
  limit?: number
  page?: number
  jobId?: string
}

type SessionVideoQuery = {
  resourceId: string
  jobId?: string | null
  startTimestamp?: number
  endTimestamp?: number
  sortOrder?: 'asc' | 'desc'
}

type SessionReplayQuery = SessionVideoQuery

type SessionProxyQuery = SessionReplayQuery

const listSessions = (axios: IFactoryFunctionRequest<OpenReplayListResponse, undefined>) => (query: ListSessionsQuery): IFactoryResponse<OpenReplayListResponse> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/sessions`,
    query,
    immediate: false,
  })
}

const filterJobs = (axios: IFactoryFunctionRequest<OpenReplayOptionsResponse, undefined>) => (query: FilterQuery): IFactoryResponse<OpenReplayOptionsResponse> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/filters/jobs`,
    query,
    immediate: false,
  })
}

const filterResources = (axios: IFactoryFunctionRequest<OpenReplayOptionsResponse, undefined>) => (query: FilterQuery): IFactoryResponse<OpenReplayOptionsResponse> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/filters/resources`,
    query,
    immediate: false,
  })
}

const getSessionVideo = (axios: IFactoryFunctionRequest<OpenReplayVideoResponse, undefined>) => (sessionId: string, query: SessionVideoQuery): IFactoryResponse<OpenReplayVideoResponse> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/sessions/${sessionId}/video`,
    query,
    immediate: true,
  })
}

const getSessionReplay = (axios: IFactoryFunctionRequest<OpenReplayReplayResponse, undefined>) => (sessionId: string, query: SessionReplayQuery): IFactoryResponse<OpenReplayReplayResponse> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/sessions/${sessionId}/replay`,
    query,
    immediate: true,
  })
}

const createSessionProxy = (axios: IFactoryFunctionRequest<OpenReplayProxyResponse, undefined>) => (sessionId: string, query: SessionProxyQuery): IFactoryResponse<OpenReplayProxyResponse> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/sessions/${sessionId}/proxy`,
    query,
    immediate: true,
  })
}

export default (axios: AxiosInstance) => ({
  listSessions: listSessions(useAxios(axios)),
  filterJobs: filterJobs(useAxios(axios)),
  filterResources: filterResources(useAxios(axios)),
  createSessionProxy: createSessionProxy(useAxios(axios)),
  getSessionReplay: getSessionReplay(useAxios(axios)),
  getSessionVideo: getSessionVideo(useAxios(axios)),
})
